
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Strategy } from "@/types/strategy-types";

export const useCreateStrategy = (loadStrategiesCallback: () => Promise<void>, providerId?: string | null) => {
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  const createStrategy = async (newStrategy: Partial<Strategy>) => {
    setIsCreating(true);
    try {
      // If user is a strategy provider, enforce their provider ID
      if (providerId) {
        console.log("Setting provider ID to:", providerId);
        newStrategy.strategy_provider_id = providerId;
      }
      
      // Ensure required fields are present before inserting
      if (!newStrategy.name || !newStrategy.exchange || !newStrategy.min_investment || 
          !newStrategy.risk_level || !newStrategy.strategy_provider_id) {
        console.error("Missing required fields:", {
          name: newStrategy.name,
          exchange: newStrategy.exchange,
          min_investment: newStrategy.min_investment,
          risk_level: newStrategy.risk_level,
          strategy_provider_id: newStrategy.strategy_provider_id
        });
        throw new Error("Missing required fields for strategy creation");
      }
      
      // Normalize plan value to ensure consistency
      const normalizedPlan = newStrategy.plan === 'premium' || newStrategy.plan === 'pro' 
        ? 'premium' 
        : 'free';
      
      // Create a new object with only the fields that belong in the strategy table
      const strategyToInsert = {
        name: newStrategy.name,
        exchange: newStrategy.exchange,
        min_investment: newStrategy.min_investment,
        risk_level: newStrategy.risk_level,
        strategy_provider_id: newStrategy.strategy_provider_id,
        plan: normalizedPlan,
        currency: newStrategy.currency || 'USDT',
        description: newStrategy.description || '',
        active: newStrategy.active !== undefined ? newStrategy.active : true,
        approved: newStrategy.approved !== undefined ? newStrategy.approved : false,
        compounding: newStrategy.compounding,
        risk_corridor: newStrategy.risk_corridor,
        expected_monthly_profit: newStrategy.expected_monthly_profit,
        defined_max_drawdown: newStrategy.defined_max_drawdown,
        product: newStrategy.product,
        traded_symbols: newStrategy.traded_symbols,
        verified_since: newStrategy.verified_since,
        social_trading: newStrategy.social_trading
      };

      console.log("Inserting strategy:", strategyToInsert);

      const { error, data } = await supabase
        .from('strategy')
        .insert(strategyToInsert)
        .select();

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      console.log("Strategy created:", data);
      await loadStrategiesCallback();
      toast({
        title: "Success",
        description: "Strategy created successfully",
      });
    } catch (error) {
      console.error('Error creating strategy:', error);
      toast({
        title: "Error",
        description: "Failed to create strategy",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  return { createStrategy, isCreating };
};
